/* DasiFrères — Ciel astro : constellation du signe + étoiles filantes aléatoires.
 *
 * Performance : deux canvases séparés.
 *   - .astro-sky-fx--constellation : dessiné UNE seule fois (et au resize). Statique
 *     → 0 coût en continu. C'est le seul endroit où l'on s'autorise shadowBlur.
 *   - .astro-sky-fx--shooting : animé UNIQUEMENT quand une étoile filante est active
 *     (sinon aucun requestAnimationFrame → 0 CPU au repos). Pas de shadowBlur dans
 *     la boucle (halo simulé par un double trait dégradé, bien moins coûteux).
 * Le champ d'étoiles fixe + scintillement reste géré en CSS (.astro-stars).
 * Respecte prefers-reduced-motion : constellation statique, pas d'étoile filante.
 */
(function () {
  'use strict';

  var cConst = document.querySelector('canvas.astro-sky-fx--constellation');
  var cShoot = document.querySelector('canvas.astro-sky-fx--shooting');
  if (!cConst || !cShoot) return;
  var gConst = cConst.getContext('2d');
  var gShoot = cShoot.getContext('2d');
  if (!gConst || !gShoot) return;

  var sign = (cConst.getAttribute('data-sign') || '').toLowerCase().trim();
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Couleur par signe (issue de astro-data.liquid), éclaircie pour rester
  // lumineuse sur le ciel noir. Défaut : or astro (#c9a84c).
  var SIGN_TINT = {
    belier: '#d94a3d', taureau: '#7a9e4f', gemeaux: '#e8c547', cancer: '#a8c8e8',
    lion: '#e89547', vierge: '#8b7355', balance: '#c8b0d4', scorpion: '#6b2e5c',
    sagittaire: '#a83250', capricorne: '#3d5c5c', verseau: '#4a9bb5', poissons: '#5c7a9b'
  };
  var tint = lighten(SIGN_TINT[sign] || '#c9a84c', 0.45);

  // Coordonnées normalisées (0..1, y vers le bas) + arêtes de liaison, calées sur
  // les VRAIES figures stellaires (positions relatives des étoiles principales).
  var CONSTELLATIONS = {
    // Bélier : γ Mesarthim – β Sheratan – α Hamal – 41 Ari (ligne brisée).
    belier: { stars: [[0.12, 0.6], [0.2, 0.52], [0.5, 0.42], [0.92, 0.3]], lines: [[0, 1], [1, 2], [2, 3]] },
    // Taureau : V des Hyades (vertex γ, Aldébaran) + 2 cornes (β Elnath, ζ) + Pléiades.
    taureau: { stars: [[0.38, 0.66], [0.5, 0.52], [0.96, 0.46], [0.4, 0.46], [0.9, 0.12], [0.1, 0.42]], lines: [[0, 1], [1, 2], [0, 3], [3, 4], [0, 5]] },
    // Gémeaux : deux silhouettes parallèles (têtes Castor & Pollux), reliées.
    gemeaux: { stars: [[0.3, 0.08], [0.33, 0.4], [0.2, 0.72], [0.12, 0.94], [0.64, 0.12], [0.62, 0.44], [0.74, 0.72], [0.82, 0.94]], lines: [[0, 1], [1, 2], [2, 3], [4, 5], [5, 6], [6, 7], [1, 5]] },
    // Cancer : Y discret (ι en haut, Acubens / β en bas).
    cancer: { stars: [[0.5, 0.1], [0.46, 0.45], [0.25, 0.78], [0.72, 0.8]], lines: [[0, 1], [1, 2], [1, 3]] },
    // Lion : la Faucille (Régulus → ε) + triangle arrière (θ, Zosma δ, Denebola β).
    lion: { stars: [[0.18, 0.64], [0.22, 0.52], [0.27, 0.4], [0.31, 0.3], [0.4, 0.22], [0.5, 0.18], [0.6, 0.55], [0.72, 0.36], [0.94, 0.46]], lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [0, 6], [6, 8], [6, 7], [7, 8], [2, 7]] },
    // Vierge : long zigzag, Spica en bas (α), bras vers β.
    vierge: { stars: [[0.4, 0.85], [0.45, 0.6], [0.5, 0.5], [0.6, 0.32], [0.2, 0.42], [0.32, 0.55], [0.62, 0.62]], lines: [[0, 1], [1, 2], [2, 3], [1, 5], [5, 4], [2, 6]] },
    // Balance : quadrilatère (β en haut, α & γ, σ & δ en bas).
    balance: { stars: [[0.52, 0.1], [0.2, 0.52], [0.82, 0.46], [0.28, 0.86], [0.9, 0.82]], lines: [[0, 1], [0, 2], [1, 3], [2, 4]] },
    // Scorpion : tête (β, δ, π) + Antarès (α) + longue queue courbée jusqu'au dard (λ).
    scorpion: { stars: [[0.15, 0.1], [0.2, 0.2], [0.18, 0.28], [0.3, 0.4], [0.36, 0.5], [0.45, 0.6], [0.55, 0.72], [0.64, 0.82], [0.74, 0.86], [0.83, 0.8], [0.87, 0.7], [0.84, 0.6], [0.77, 0.55]], lines: [[0, 1], [1, 2], [1, 3], [3, 4], [4, 5], [5, 6], [6, 7], [7, 8], [8, 9], [9, 10], [10, 11], [11, 12]] },
    // Sagittaire : la Théière (couvercle λ, corps, bec γ, anse).
    sagittaire: { stars: [[0.5, 0.18], [0.42, 0.38], [0.18, 0.52], [0.4, 0.72], [0.56, 0.72], [0.66, 0.5], [0.6, 0.36]], lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 6], [6, 0], [6, 1]] },
    // Capricorne : voile/triangle large (de α-β à δ Deneb Algedi).
    capricorne: { stars: [[0.12, 0.3], [0.32, 0.6], [0.5, 0.72], [0.68, 0.66], [0.82, 0.45], [0.9, 0.32]], lines: [[0, 1], [1, 2], [2, 3], [3, 4], [4, 5], [5, 0]] },
    // Verseau : cruche (β, α, γ, ζ, η) + filet d'eau qui descend.
    verseau: { stars: [[0.15, 0.3], [0.4, 0.32], [0.55, 0.28], [0.6, 0.36], [0.66, 0.3], [0.62, 0.55], [0.7, 0.7]], lines: [[0, 1], [1, 2], [2, 3], [3, 4], [3, 5], [5, 6]] },
    // Poissons : cercle (Circlet) + deux cordons reliés au nœud α Alrescha.
    poissons: { stars: [[0.1, 0.35], [0.22, 0.3], [0.3, 0.4], [0.5, 0.5], [0.7, 0.55], [0.8, 0.4], [0.9, 0.25]], lines: [[0, 1], [1, 2], [2, 0], [2, 3], [3, 4], [4, 5], [5, 6]] }
  };

  // DPR plafonné à 1.5 : suffisant visuellement, divise le nombre de pixels à
  // remplir par ~1.8 vs 2.0 → boucle d'animation plus légère.
  var dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 1.5));
  var W = 0;
  var H = 0;
  var constellation = CONSTELLATIONS[sign] || null;
  var shooters = [];
  var looping = false;

  function sizeCanvas(canvas, c) {
    canvas.width = Math.floor(W * dpr);
    canvas.height = Math.floor(H * dpr);
    canvas.style.width = W + 'px';
    canvas.style.height = H + 'px';
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function resize() {
    W = window.innerWidth;
    H = window.innerHeight;
    sizeCanvas(cConst, gConst);
    sizeCanvas(cShoot, gShoot);
    drawConstellation();
  }

  function constellationBox() {
    var size = Math.min(Math.min(W, H) * 0.5, 460);
    var cx = W * (W < 760 ? 0.5 : 0.72);
    var cy = H * (W < 760 ? 0.2 : 0.26);
    return { x: cx - size / 2, y: cy - size / 2, s: size };
  }

  // Dessiné une seule fois (statique). shadowBlur acceptable ici (pas par frame).
  function drawConstellation() {
    gConst.clearRect(0, 0, W, H);
    if (!constellation) return;
    var b = constellationBox();
    var pts = constellation.stars.map(function (p) {
      return [b.x + p[0] * b.s, b.y + p[1] * b.s];
    });

    gConst.save();
    gConst.strokeStyle = toRgba(tint, 0.22);
    gConst.lineWidth = 1;
    gConst.shadowColor = toRgba(tint, 0.5);
    gConst.shadowBlur = 6;
    constellation.lines.forEach(function (e) {
      var a = pts[e[0]];
      var c = pts[e[1]];
      if (!a || !c) return;
      gConst.beginPath();
      gConst.moveTo(a[0], a[1]);
      gConst.lineTo(c[0], c[1]);
      gConst.stroke();
    });
    gConst.restore();

    pts.forEach(function (p) {
      gConst.save();
      gConst.shadowColor = toRgba(tint, 0.9);
      gConst.shadowBlur = 12;
      gConst.fillStyle = 'rgba(255,255,255,0.95)';
      gConst.beginPath();
      gConst.arc(p[0], p[1], 2.4, 0, Math.PI * 2);
      gConst.fill();
      gConst.restore();
    });
  }

  // ───── Étoiles filantes (canvas dédié, animé seulement quand actif) ─────
  function spawnShooter() {
    var fromLeft = Math.random() < 0.5;
    var startX = fromLeft ? Math.random() * W * 0.5 : W * (0.5 + Math.random() * 0.5);
    var startY = Math.random() * H * 0.4;
    var angle = fromLeft ? (Math.PI / 5 + Math.random() * 0.25) : (Math.PI - Math.PI / 5 - Math.random() * 0.25);
    var speed = 9 + Math.random() * 7;
    shooters.push({
      x: startX, y: startY,
      vx: Math.cos(angle) * speed, vy: Math.sin(angle) * speed,
      len: 90 + Math.random() * 120, life: 0, max: 55 + Math.random() * 30
    });
    if (!looping) {
      looping = true;
      requestAnimationFrame(loop);
    }
  }

  function scheduleShooter() {
    if (reduce) return;
    var delay = 4000 + Math.random() * 8000;
    window.setTimeout(function () {
      if (!document.hidden) spawnShooter();
      scheduleShooter();
    }, delay);
  }

  function loop() {
    gShoot.clearRect(0, 0, W, H);
    var alive = [];
    for (var i = 0; i < shooters.length; i++) {
      var s = shooters[i];
      s.x += s.vx;
      s.y += s.vy;
      s.life++;
      var inv = 1 / Math.hypot(s.vx, s.vy);
      var tx = s.x - s.vx * inv * s.len;
      var ty = s.y - s.vy * inv * s.len;
      var fade = 1 - s.life / s.max;
      if (fade > 0 && s.x > -160 && s.x < W + 160 && s.y < H + 160) {
        var grad = gShoot.createLinearGradient(s.x, s.y, tx, ty);
        grad.addColorStop(0, 'rgba(255,255,255,' + (0.95 * fade) + ')');
        grad.addColorStop(0.4, toRgba(tint, 0.45 * fade));
        grad.addColorStop(1, 'rgba(255,255,255,0)');
        // Halo : trait large très translucide (remplace shadowBlur, bien plus léger).
        gShoot.strokeStyle = grad;
        gShoot.lineCap = 'round';
        gShoot.lineWidth = 5;
        gShoot.globalAlpha = 0.35;
        gShoot.beginPath();
        gShoot.moveTo(s.x, s.y);
        gShoot.lineTo(tx, ty);
        gShoot.stroke();
        // Cœur net.
        gShoot.globalAlpha = 1;
        gShoot.lineWidth = 1.8;
        gShoot.beginPath();
        gShoot.moveTo(s.x, s.y);
        gShoot.lineTo(tx, ty);
        gShoot.stroke();
        alive.push(s);
      }
    }
    gShoot.globalAlpha = 1;
    shooters = alive;
    if (shooters.length) {
      requestAnimationFrame(loop);
    } else {
      looping = false;
      gShoot.clearRect(0, 0, W, H);
    }
  }

  function lighten(hex, amount) {
    var h = (hex || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return '#c9a84c';
    var r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
    r = Math.round(r + (255 - r) * amount);
    g = Math.round(g + (255 - g) * amount);
    b = Math.round(b + (255 - b) * amount);
    return 'rgb(' + r + ',' + g + ',' + b + ')';
  }

  function toRgba(col, a) {
    if (col && col.indexOf('rgb') === 0) {
      var m = col.match(/\d+/g) || [201, 168, 76];
      return 'rgba(' + m[0] + ',' + m[1] + ',' + m[2] + ',' + a + ')';
    }
    var h = (col || '').replace('#', '');
    if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
    var n = parseInt(h, 16);
    if (isNaN(n)) return 'rgba(201,168,76,' + a + ')';
    return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + a + ')';
  }

  // Resize debounce léger.
  var resizeTimer = null;
  window.addEventListener('resize', function () {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(resize, 150);
  });

  resize();
  scheduleShooter();
})();
